import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit, Trash2, Loader2, X, Check, FileText, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay, useDraggable, useDroppable, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";

interface HealthArea {
  id: string;
  name: string;
}

interface TemplateItem {
  id: string;
  name: string;
  is_active: boolean;
  display_order: number;
}

interface Template {
  id: string;
  title: string;
  health_area_id: string;
  health_area?: HealthArea;
  items: TemplateItem[];
  created_at: string;
}

// Draggable Item Component
function DraggableTemplateItem({
  item,
  isOver,
  editingItem,
  editingName,
  setEditingName,
  setEditingItem,
  updateItemName,
  toggleItemActive,
  deleteItem
}: {
  item: TemplateItem;
  isOver: boolean;
  editingItem: string | null;
  editingName: string;
  setEditingName: (name: string) => void;
  setEditingItem: (id: string | null) => void;
  updateItemName: (id: string) => void;
  toggleItemActive: (id: string, value: boolean) => void;
  deleteItem: (id: string, name: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    data: { item }
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center justify-between gap-3 p-3 border rounded-lg transition-all",
        isDragging && "opacity-40 scale-95",
        !isDragging && "hover:bg-accent/50"
      )}
    >
      {editingItem === item.id ? (
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Input
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            className="flex-1 min-w-0"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') updateItemName(item.id);
              if (e.key === 'Escape') setEditingItem(null);
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => updateItemName(item.id)}
            className="shrink-0"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditingItem(null)}
            className="shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
            <div
              {...listeners}
              {...attributes}
              className="cursor-grab active:cursor-grabbing touch-none shrink-0"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <span className="font-medium truncate">{item.name}</span>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="flex items-center gap-2">
              <Switch
                checked={item.is_active}
                onCheckedChange={() => toggleItemActive(item.id, item.is_active)}
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {item.is_active ? "Ativo" : "Inativo"}
              </span>
            </div>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingItem(item.id);
                  setEditingName(item.name);
                }}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => deleteItem(item.id, item.name)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Templates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [healthAreas, setHealthAreas] = useState<HealthArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [addingTemplate, setAddingTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<Template | null>(null);
  const [newTemplateTitle, setNewTemplateTitle] = useState("");
  const [newTemplateHealthArea, setNewTemplateHealthArea] = useState<string>("");
  const [editingTemplateTitle, setEditingTemplateTitle] = useState("");
  const [editingTemplateHealthArea, setEditingTemplateHealthArea] = useState<string>("");
  const [addingItem, setAddingItem] = useState<string | null>(null);
  const [newItemName, setNewItemName] = useState("");
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [activeItem, setActiveItem] = useState<TemplateItem | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [creatingItem, setCreatingItem] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    loadHealthAreas();
    loadTemplates();
  }, []);

  async function loadHealthAreas() {
    try {
      const { data, error } = await supabase
        .from('health_areas')
        .select('id, name')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setHealthAreas(data || []);
    } catch (error) {
      console.error('Error loading health areas:', error);
      toast.error('Erro ao carregar especialidades');
    }
  }

  async function loadTemplates() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('category_templates')
        .select(`
          id,
          title,
          health_area_id,
          created_at,
          health_areas:health_area_id (
            id,
            name
          ),
          items:category_template_items (
            id,
            name,
            is_active,
            display_order
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedTemplates = (data || []).map((template: any) => ({
        ...template,
        health_area: template.health_areas,
        items: (template.items || []).sort((a: TemplateItem, b: TemplateItem) => a.display_order - b.display_order)
      }));

      setTemplates(formattedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  }

  async function createTemplate() {
    if (!newTemplateTitle.trim()) {
      toast.error('Digite um título para o template');
      return;
    }
    if (!newTemplateHealthArea) {
      toast.error('Selecione uma especialidade');
      return;
    }

    setCreatingTemplate(true);
    try {
      const { data, error } = await supabase
        .from('category_templates')
        .insert({
          title: newTemplateTitle.trim(),
          health_area_id: newTemplateHealthArea,
        })
        .select()
        .single();

      if (error) throw error;

      setAddingTemplate(false);
      setNewTemplateTitle("");
      setNewTemplateHealthArea("");
      await loadTemplates();
      toast.success('Template criado com sucesso');
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Erro ao criar template');
    } finally {
      setCreatingTemplate(false);
    }
  }

  async function updateTemplate(templateId: string) {
    if (!editingTemplateTitle.trim()) {
      toast.error('Digite um título para o template');
      return;
    }
    if (!editingTemplateHealthArea) {
      toast.error('Selecione uma especialidade');
      return;
    }

    try {
      const { error } = await supabase
        .from('category_templates')
        .update({
          title: editingTemplateTitle.trim(),
          health_area_id: editingTemplateHealthArea,
        })
        .eq('id', templateId);

      if (error) throw error;

      setEditingTemplate(null);
      setEditingTemplateTitle("");
      setEditingTemplateHealthArea("");
      await loadTemplates();
      toast.success('Template atualizado com sucesso');
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Erro ao atualizar template');
    }
  }

  async function deleteTemplate(template: Template) {
    try {
      const { error } = await supabase
        .from('category_templates')
        .delete()
        .eq('id', template.id);

      if (error) throw error;

      setDeletingTemplate(null);
      if (selectedTemplate?.id === template.id) {
        setSelectedTemplate(null);
      }
      await loadTemplates();
      toast.success('Template excluído com sucesso');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Erro ao excluir template');
    }
  }

  async function addItem(templateId: string) {
    if (!newItemName.trim()) {
      toast.error('Digite um nome para o item');
      return;
    }

    setCreatingItem(true);
    try {
      const template = templates.find(t => t.id === templateId);
      const maxOrder = template?.items.length > 0
        ? Math.max(...template.items.map(i => i.display_order))
        : 0;

      const { error } = await supabase
        .from('category_template_items')
        .insert({
          template_id: templateId,
          name: newItemName.trim(),
          is_active: true,
          display_order: maxOrder + 1,
        });

      if (error) throw error;

      setAddingItem(null);
      setNewItemName("");
      await loadTemplates();
      toast.success('Item adicionado com sucesso');
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Erro ao adicionar item');
    } finally {
      setCreatingItem(false);
    }
  }

  async function updateItemName(itemId: string) {
    if (!editingName.trim()) {
      toast.error('Digite um nome para o item');
      return;
    }

    try {
      const { error } = await supabase
        .from('category_template_items')
        .update({ name: editingName.trim() })
        .eq('id', itemId);

      if (error) throw error;

      setEditingItem(null);
      setEditingName("");
      await loadTemplates();
      toast.success('Item atualizado com sucesso');
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Erro ao atualizar item');
    }
  }

  async function toggleItemActive(itemId: string, currentValue: boolean) {
    try {
      const { error } = await supabase
        .from('category_template_items')
        .update({ is_active: !currentValue })
        .eq('id', itemId);

      if (error) throw error;

      await loadTemplates();
      toast.success(`Item ${!currentValue ? 'ativado' : 'desativado'} com sucesso`);
    } catch (error) {
      console.error('Error toggling item:', error);
      toast.error('Erro ao atualizar item');
    }
  }

  async function deleteItem(itemId: string, itemName: string) {
    if (!confirm(`Tem certeza que deseja excluir o item "${itemName}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('category_template_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      await loadTemplates();
      toast.success('Item excluído com sucesso');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Erro ao excluir item');
    }
  }

  async function reorderItems(templateId: string, newOrder: TemplateItem[]) {
    try {
      for (const [index, item] of newOrder.entries()) {
        const { error } = await supabase
          .from('category_template_items')
          .update({ display_order: index + 1 })
          .eq('id', item.id);

        if (error) throw error;
      }

      await loadTemplates();
      toast.success('Ordem dos itens atualizada');
    } catch (error) {
      console.error('Error reordering items:', error);
      toast.error('Erro ao reordenar itens');
      await loadTemplates();
    }
  }

  function handleDragStart(event: DragStartEvent) {
    const { active } = event;
    const activeId = active.id as string;
    
    const template = templates.find(t => t.id === selectedTemplate?.id);
    const item = template?.items.find(i => i.id === activeId);
    if (item) {
      setActiveItem(item);
    }
  }

  function handleDragOver(event: any) {
    const { over } = event;
    setOverId(over ? over.id as string : null);
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    
    setActiveItem(null);
    setOverId(null);

    if (!over || !selectedTemplate) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    if (activeId === overId) return;

    const template = templates.find(t => t.id === selectedTemplate.id);
    if (!template) return;

    const oldIndex = template.items.findIndex(item => item.id === activeId);
    const newIndex = template.items.findIndex(item => item.id === overId);

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      const newOrder = arrayMove(template.items, oldIndex, newIndex);
      await reorderItems(template.id, newOrder);
    }
  }

  const selectedTemplateData = templates.find(t => t.id === selectedTemplate?.id);
  const itemIds = selectedTemplateData?.items.map(item => item.id) || [];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Templates de Categorias</h1>
          <p className="text-muted-foreground">
            Gerencie templates de categorias para diferentes especialidades
          </p>
        </div>

        {loading ? (
          <Card>
            <CardContent className="py-8">
              <Skeleton className="h-8 w-full mb-4" />
              <Skeleton className="h-8 w-full mb-4" />
              <Skeleton className="h-8 w-full" />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lista de Templates */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Templates</CardTitle>
                    <Button
                      size="sm"
                      onClick={() => {
                        setAddingTemplate(true);
                        setNewTemplateTitle("");
                        setNewTemplateHealthArea("");
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Novo
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {addingTemplate ? (
                    <div className="space-y-3 p-3 border rounded-lg mb-3">
                      <Input
                        value={newTemplateTitle}
                        onChange={(e) => setNewTemplateTitle(e.target.value)}
                        placeholder="Título do template"
                        autoFocus
                        disabled={creatingTemplate}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !creatingTemplate) createTemplate();
                          if (e.key === 'Escape') setAddingTemplate(false);
                        }}
                      />
                      <Select
                        value={newTemplateHealthArea}
                        onValueChange={setNewTemplateHealthArea}
                        disabled={creatingTemplate}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione a especialidade" />
                        </SelectTrigger>
                        <SelectContent>
                          {healthAreas.map((area) => (
                            <SelectItem key={area.id} value={area.id}>
                              {area.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={createTemplate}
                          disabled={creatingTemplate}
                          className="flex-1"
                        >
                          {creatingTemplate ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setAddingTemplate(false);
                            setNewTemplateTitle("");
                            setNewTemplateHealthArea("");
                          }}
                          disabled={creatingTemplate}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  <div className="space-y-2">
                    {templates.map((template) => (
                      <div
                        key={template.id}
                        className={cn(
                          "p-3 border rounded-lg cursor-pointer transition-colors",
                          selectedTemplate?.id === template.id
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-accent"
                        )}
                        onClick={() => setSelectedTemplate(template)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{template.title}</p>
                            <p className="text-xs opacity-70 truncate">
                              {template.health_area?.name || 'Sem especialidade'}
                            </p>
                          </div>
                          <div className="flex gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingTemplate(template.id);
                                setEditingTemplateTitle(template.title);
                                setEditingTemplateHealthArea(template.health_area_id);
                              }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeletingTemplate(template)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detalhes do Template */}
            <div className="lg:col-span-2">
              {selectedTemplate ? (
                <Card>
                  <CardHeader>
                    {editingTemplate === selectedTemplate.id ? (
                      <div className="space-y-3">
                        <Input
                          value={editingTemplateTitle}
                          onChange={(e) => setEditingTemplateTitle(e.target.value)}
                          placeholder="Título do template"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') updateTemplate(selectedTemplate.id);
                            if (e.key === 'Escape') {
                              setEditingTemplate(null);
                              setEditingTemplateTitle("");
                              setEditingTemplateHealthArea("");
                            }
                          }}
                        />
                        <Select
                          value={editingTemplateHealthArea}
                          onValueChange={setEditingTemplateHealthArea}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a especialidade" />
                          </SelectTrigger>
                          <SelectContent>
                            {healthAreas.map((area) => (
                              <SelectItem key={area.id} value={area.id}>
                                {area.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => updateTemplate(selectedTemplate.id)}>
                            <Check className="h-4 w-4 mr-2" />
                            Salvar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingTemplate(null);
                              setEditingTemplateTitle("");
                              setEditingTemplateHealthArea("");
                            }}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>{selectedTemplate.title}</CardTitle>
                          <CardDescription>
                            {selectedTemplate.health_area?.name || 'Sem especialidade'}
                          </CardDescription>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingTemplate(selectedTemplate.id);
                            setEditingTemplateTitle(selectedTemplate.title);
                            setEditingTemplateHealthArea(selectedTemplate.health_area_id);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {selectedTemplateData && (
                        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                          {selectedTemplateData.items.map((item) => (
                            <DraggableTemplateItem
                              key={item.id}
                              item={item}
                              isOver={overId === item.id}
                              editingItem={editingItem}
                              editingName={editingName}
                              setEditingName={setEditingName}
                              setEditingItem={setEditingItem}
                              updateItemName={updateItemName}
                              toggleItemActive={toggleItemActive}
                              deleteItem={deleteItem}
                            />
                          ))}
                        </SortableContext>
                      )}

                      {addingItem === selectedTemplate.id ? (
                        <div className="flex items-center gap-2 p-3 border rounded-lg bg-accent/30">
                          <Input
                            value={newItemName}
                            onChange={(e) => setNewItemName(e.target.value)}
                            placeholder="Nome do novo item"
                            className="flex-1"
                            autoFocus
                            disabled={creatingItem}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !creatingItem) addItem(selectedTemplate.id);
                              if (e.key === 'Escape') setAddingItem(null);
                            }}
                          />
                          <Button
                            size="sm"
                            onClick={() => addItem(selectedTemplate.id)}
                            disabled={creatingItem}
                          >
                            {creatingItem ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setAddingItem(null);
                              setNewItemName("");
                            }}
                            disabled={creatingItem}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => setAddingItem(selectedTemplate.id)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Adicionar item
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Selecione um template para ver seus itens</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Dialog de confirmação de exclusão */}
        <AlertDialog open={!!deletingTemplate} onOpenChange={(open) => !open && setDeletingTemplate(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja excluir o template "{deletingTemplate?.title}"?
                Esta ação também excluirá todos os itens associados e não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deletingTemplate && deleteTemplate(deletingTemplate)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <DragOverlay>
        {activeItem && (
          <div className="flex items-center gap-2 p-3 border rounded-lg bg-background shadow-lg opacity-90">
            <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium truncate">{activeItem.name}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

